import {ActionIcon} from "@mantine/core";
import {TrashIcon, DownloadIcon} from "@phosphor-icons/react";
import {useMemo} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import api from '../../utils/api';
import CGT from "../../utils/CGT";

async function fetchLogs() {
    const {data} = await api.get('/admin/logs');
    const list = (data && data.files) ? data.files : [];
    list.sort((a, b) => (b.modified || 0) - (a.modified || 0));
    return list;
}

export default function Logs() {
    const queryClient = useQueryClient();

    const {data: files = [], isLoading, isError, error} = useQuery({
        queryKey: ['logs'],
        queryFn: fetchLogs,
        staleTime: 1000 * 30,
        refetchOnWindowFocus: false,
    });

    const deleteMutation = useMutation({
        mutationFn: async (name) => {
            await api.delete(`/admin/logs/${encodeURIComponent(name)}`);
            return name;
        },
        // Optimistically update cache
        onMutate: async (name) => {
            await queryClient.cancelQueries({queryKey: ['logs']});
            const previous = queryClient.getQueryData(['logs']);
            queryClient.setQueryData(['logs'], (old = []) => old.filter((f) => f.name !== name));
            return {previous};
        },
        onError: (_err, _name, context) => {
            if (context?.previous) {
                queryClient.setQueryData(['logs'], context.previous);
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries({queryKey: ['logs']});
        },
    });

    const fmtSize = (bytes) => {
        if (typeof bytes !== 'number') return '-';
        const units = ['B','KB','MB','GB','TB'];
        let i = 0;
        let n = bytes;
        while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
        return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
    };

    const fmtDate = (ts) => {
        if (!ts) return '-';
        const d = CGT.fromSeconds(ts);
        return `${d.toLocaleString()} - ${d.toRelative()}`;
    };

    const handleDelete = (name) => {
        if (!window.confirm(`Delete log file "${name}"? This cannot be undone.`)) return;
        deleteMutation.mutate(name);
    };

    const content = useMemo(() => {
        if (isLoading) return <div>Loading logs…</div>;
        if (isError) return <div style={{color: 'red'}}>Error: {error?.message || 'Failed to load logs'}</div>;
        if (!files.length) return <div>No log files found.</div>;

        const totalSize = files.reduce((acc, f) => acc + f.size, 0);
        return (
            <table className="table" style={{width:'100%', borderCollapse:'collapse'}}>
                <thead>
                <tr>
                    <th style={{textAlign:'left', borderBottom:'1px solid #ccc', padding:'8px'}}>File</th>
                    <th style={{textAlign:'right', borderBottom:'1px solid #ccc', padding:'8px'}}>Size</th>
                    <th style={{textAlign:'left', borderBottom:'1px solid #ccc', padding:'8px'}}>Last Modified</th>
                    <th style={{borderBottom:'1px solid #ccc', padding:'8px'}}></th>
                </tr>
                </thead>
                <tbody>
                {files.map(f => {
                    const href = `${import.meta.env.VITE_BACKEND_URL}api/admin/logs/${encodeURIComponent(f.name)}`;
                    return (
                        <tr key={f.name}>
                            <td style={{padding:'8px'}}>{f.name}</td>
                            <td style={{padding:'8px', textAlign:'right'}}>{fmtSize(f.size)}</td>
                            <td style={{padding:'8px'}}>{fmtDate(f.modified)}</td>
                            <td style={{padding:'8px'}}>
                                <div className="flex flex-row gap-2">
                                <a href={href} download>
                                    <ActionIcon variant="subtle" title="Download"><DownloadIcon size={16}/></ActionIcon>
                                </a>
                                <ActionIcon variant="subtle" onClick={() => handleDelete(f.name)} title="Delete" disabled={deleteMutation.isPending}><TrashIcon size={16}/></ActionIcon>
                                </div>
                            </td>
                        </tr>
                    );
                })}
                </tbody>
                <tfoot>
                <tr>
                    <th className="text-left">Total size</th>
                    <th className="text-right">{fmtSize(totalSize)}</th>
                </tr>
                </tfoot>
            </table>
        );
    }, [files, isLoading, isError, error, deleteMutation.isPending]);

    return (
        <div style={{maxWidth:900, margin:'20px auto'}}>
            <title>Admin - Server Logs</title>
            <h2>Server Logs</h2>
            {content}
        </div>
    );
}
